/**
 * TerrainTSLNoise — TSL (Three Shading Language) GPU noise node library.
 *
 * Ports the CPU noise functions from TerrainNoiseUtils.ts to TSL Fn nodes for
 * use in WebGPU / WebGL 2 shader materials. Each function is a pure TSL node
 * graph that can be composed into larger material graphs.
 *
 * Based on techniques by Inigo Quilez:
 *   https://iquilezles.org/articles/morenoise/
 *   https://iquilezles.org/articles/fbm/
 *   https://iquilezles.org/articles/smoothvoronoi/
 *
 * IMPORTANT: TSL does not support runtime loops inside Fn bodies. All
 * multi-octave functions use compile-time JS for loops that unroll into a flat
 * node graph. Per-iteration `smoothstep` guards allow the caller to control the
 * effective octave count at runtime via a float uniform.
 *
 * NOTE: The hash function uses a two-step fract+sin+fract approach that
 * pre-folds large coordinates into a safe range, avoiding the GPU sin()
 * precision degradation that causes visible grid artifacts at large world
 * coordinates (>256 units).
 */

import { Fn, float, vec2, vec3, fract, floor, abs, sin, dot, smoothstep, min, sqrt } from 'three/tsl';

// ─── Internal rotation constants ──────────────────────────────────────────────
// ~37° inter-octave rotation breaks axis-aligned grid artifacts in fBm.
const FBM_COS = 0.8;
const FBM_SIN = 0.6;

// ─── Hash Functions ───────────────────────────────────────────────────────────

/**
 * tslHash21 — maps a vec2 lattice coordinate to a pseudo-random float in [0, 1].
 *
 * Uses a two-step hash: first fract-folds the input to prevent large-value
 * sin() precision loss, then applies sin(dot) * 43758.5453.
 *
 * @param p - vec2 input coordinate
 * @returns float in [0, 1]
 */
export const tslHash21 = /*#__PURE__*/ Fn(([p]: any) => {
  const pv = vec2(p).toVar();
  // Pre-fold to avoid large-value sin() precision issues
  const folded = fract(pv.mul(vec2(0.1031, 0.1030))).toVar();
  // Self-feedback: add swizzled version to decorrelate
  folded.addAssign(vec2(dot(folded, vec2(33.33, 43.43))));
  return fract(folded.x.add(folded.y).mul(folded.x).mul(43758.5453));
});

/**
 * tslHash22 — maps a vec2 lattice coordinate to two pseudo-random floats in [0, 1].
 *
 * Each component uses an independent fold coefficient pair.
 *
 * @param p - vec2 input coordinate
 * @returns vec2 in [0, 1]²
 */
 
export const tslHash22: any = /*#__PURE__*/ Fn(([p]: any) => {
  const pv = vec2(p).toVar();
  // Dave Hoskins-style hash: fract-fold then self-feedback
  const p3 = fract(vec3(pv.x, pv.y, pv.x).mul(vec3(0.1031, 0.1030, 0.0973))).toVar();
  const dp = dot(p3, vec3(p3.y, p3.z, p3.x).add(vec3(33.33, 33.33, 33.33)));
  return fract(vec2(p3.x.add(p3.y), p3.x.add(p3.z)).mul(dp));
});

// ─── Internal seeded lattice hash ─────────────────────────────────────────────
// Pre-folds large integer coordinates before hashing to prevent sin() precision
// artifacts. The seed is mixed in via addition before folding.

 
const _tslHashLattice: any = /*#__PURE__*/ Fn(([ix, iz, seed]: any) => {
  const sx = float(ix).add(float(seed));
  const sz = float(iz).add(float(seed).mul(1.7));
  // Pre-fold: bring large coordinates into [0, 1) range before sin()
  const fx = fract(sx.mul(0.1031));
  const fz = fract(sz.mul(0.1030));
  // Self-feedback step to decorrelate
  const d = fx.add(fz).add(fx.mul(fz)).mul(33.33);
  // Final hash
  const d2 = fx.mul(127.1).add(fz.mul(311.7)).add(d);
  return fract(sin(d2).mul(43758.5453));
});

// ─── Value Noise ──────────────────────────────────────────────────────────────

/**
 * tslValueNoise — 2D value noise with quintic interpolation.
 *
 * Samples random values at integer lattice corners and blends with the quintic
 * ease curve u(t) = 6t^5 - 15t^4 + 10t^3 for C2 continuity.
 *
 * @param p    - vec2 position
 * @param seed - float seed (distinguishes independent noise layers)
 * @returns float in [-1, 1]
 */
 
export const tslValueNoise: any = /*#__PURE__*/ Fn(([p, seed]: any) => {
  const pv = vec2(p).toVar();
  const s  = float(seed);

  // Integer cell and fractional position
  const pi = floor(pv).toVar();
  const pf = pv.sub(pi).toVar();               // fract(pv)

  const fx = pf.x.toVar();
  const fz = pf.y.toVar();

  // Quintic weights: u(t) = t³(6t² - 15t + 10)
  const ux = fx.mul(fx).mul(fx).mul(fx.mul(fx.mul(6.0).sub(15.0)).add(10.0)).toVar();
  const uz = fz.mul(fz).mul(fz).mul(fz.mul(fz.mul(6.0).sub(15.0)).add(10.0)).toVar();

  // Four lattice corner values in [0, 1]
  const a = _tslHashLattice(pi.x,              pi.y,              s).toVar();
  const b = _tslHashLattice(pi.x.add(1.0),     pi.y,              s).toVar();
  const c = _tslHashLattice(pi.x,              pi.y.add(1.0),     s).toVar();
  const d = _tslHashLattice(pi.x.add(1.0),     pi.y.add(1.0),     s).toVar();

  // Bilinear blend of corners then remap [0,1] → [-1,1]
  const mixed = a
    .add(b.sub(a).mul(ux))
    .add(c.sub(a).mul(uz))
    .add(a.sub(b).sub(c).add(d).mul(ux).mul(uz));

  return mixed.mul(2.0).sub(1.0);   // [0,1] → [-1,1]
});

/**
 * tslValueNoiseD — 2D value noise with quintic interpolation AND analytical
 * partial derivatives.
 *
 * Returns vec3(value, dx, dz):
 *   - x: same as tslValueNoise
 *   - y: ∂N/∂x
 *   - z: ∂N/∂z
 *
 * Derivatives use the chain rule through the quintic:
 *   u'(t) = 30 * t² * (t² - 2t + 1)
 *
 * @param p    - vec2 position
 * @param seed - float seed
 * @returns vec3(value, dx, dz)
 */
 
export const tslValueNoiseD: any = /*#__PURE__*/ Fn(([p, seed]: any) => {
  const pv = vec2(p).toVar();
  const s  = float(seed);

  const pi = floor(pv).toVar();
  const pf = pv.sub(pi).toVar();

  const fx = pf.x.toVar();
  const fz = pf.y.toVar();

  // Quintic weights
  const ux  = fx.mul(fx).mul(fx).mul(fx.mul(fx.mul(6.0).sub(15.0)).add(10.0)).toVar();
  const uz  = fz.mul(fz).mul(fz).mul(fz.mul(fz.mul(6.0).sub(15.0)).add(10.0)).toVar();

  // Quintic derivatives: u'(t) = 30t²(t² - 2t + 1)
  const dux = fx.mul(fx).mul(fx.mul(fx.sub(2.0)).add(1.0)).mul(30.0).toVar();
  const duz = fz.mul(fz).mul(fz.mul(fz.sub(2.0)).add(1.0)).mul(30.0).toVar();

  // Four corner hashes
  const a = _tslHashLattice(pi.x,              pi.y,              s).toVar();
  const b = _tslHashLattice(pi.x.add(1.0),     pi.y,              s).toVar();
  const c = _tslHashLattice(pi.x,              pi.y.add(1.0),     s).toVar();
  const d = _tslHashLattice(pi.x.add(1.0),     pi.y.add(1.0),     s).toVar();

  // Bilinear coefficients
  const k0 = a.toVar();
  const k1 = b.sub(a).toVar();
  const k2 = c.sub(a).toVar();
  const k4 = a.sub(b).sub(c).add(d).toVar();

  // Value remapped to [-1,1]
  const value = k0.add(k1.mul(ux)).add(k2.mul(uz)).add(k4.mul(ux).mul(uz))
    .mul(2.0).sub(1.0);

  // Analytical partial derivatives (factor 2 from the [-1,1] remap)
  const dx = dux.mul(k1.add(k4.mul(uz))).mul(2.0);
  const dz = duz.mul(k2.add(k4.mul(ux))).mul(2.0);

  return vec3(value, dx, dz);
});

// ─── fBm Variants ─────────────────────────────────────────────────────────────

/**
 * tslFbm — standard fractional Brownian motion.
 *
 * Sums noise layers with halving amplitude and doubling frequency each octave.
 * The inter-octave rotation (~37°) breaks axis-aligned grid artifacts.
 *
 * TSL does not support runtime loops, so the body is statically unrolled to
 * 12 iterations. A per-iteration `smoothstep` guard soft-disables iterations
 * beyond the `oct` parameter, enabling fractional octave blending.
 *
 * @param p    - vec2 position
 * @param seed - float seed
 * @param oct  - float octave count (runtime; 1–12)
 * @returns float (sum of octave contributions)
 */
export const tslFbm = /*#__PURE__*/ Fn(([p, seed, oct]: any) => {
  const cp  = vec2(p).toVar();       // current position (rotated each octave)
  const amp = float(1.0).toVar();    // per-octave amplitude
  const a   = float(0.0).toVar();    // accumulated height
  // Running sum of amplitude contributions — used to normalise the
  // accumulation so the return range is approximately [-1, 1] regardless
  // of the requested octave count. The previous implementation returned
  // the un-normalised sum, which grew with octaves (≈1.75 at 3 oct vs
  // ≈2.0 at 12 oct) and made low/high quality presets produce visibly
  // different brightness on the same terrain.
  const ampSum = float(0.0).toVar();

  for (let i = 0; i < 12; i++) {
    // Soft-disable this iteration if i >= oct (guard in [0,1])
    const guard = smoothstep(float(i - 0.5), float(i + 0.5), float(oct));

    const val = tslValueNoise(cp, float(seed));
    a.addAssign(amp.mul(val).mul(guard));
    ampSum.addAssign(amp.mul(guard));
    amp.mulAssign(0.5);

    // Rotate by ~37° then double frequency: p' = M * p * 2
    //   M = [[cos, -sin], [sin, cos]]  with cos=0.8, sin=0.6
    const rx = cp.x.mul(FBM_COS).sub(cp.y.mul(FBM_SIN)).mul(2.0).toVar();
    const rz = cp.x.mul(FBM_SIN).add(cp.y.mul(FBM_COS)).mul(2.0).toVar();
    cp.assign(vec2(rx, rz));
  }

  // Divide by the amplitude sum to normalise to the value-noise domain.
  // Clamp the divisor to avoid division-by-zero in the impossible oct=0
  // path (the guard keeps all contributions at 0 anyway).
  return a.div(ampSum.max(float(1e-5)));
});

/**
 * tslFbmEroded — IQ derivative-accumulated fBm.
 *
 * Accumulated derivatives from each octave dampen high-frequency detail in
 * steep areas, creating smooth ridges and detailed valleys:
 *   a += amp * val / (1 + dx² + dz²)
 *
 * Unrolled to 12 iterations with the same smoothstep guard as tslFbm.
 *
 * @param p    - vec2 position
 * @param seed - float seed
 * @param oct  - float octave count (runtime; 1–12)
 * @returns float (eroded height accumulation)
 */
export const tslFbmEroded = /*#__PURE__*/ Fn(([p, seed, oct]: any) => {
  const cp    = vec2(p).toVar();
  const amp   = float(1.0).toVar();
  const a     = float(0.0).toVar();
  const accDx = float(0.0).toVar();   // accumulated derivative X
  const accDz = float(0.0).toVar();   // accumulated derivative Z

  for (let i = 0; i < 12; i++) {
    const guard = smoothstep(float(i - 0.5), float(i + 0.5), float(oct));

    const nd  = tslValueNoiseD(cp, float(seed));  // vec3(val, dx, dz)
    const val = nd.x.toVar();
    const ndx = nd.y.toVar();
    const ndz = nd.z.toVar();

    accDx.addAssign(ndx);
    accDz.addAssign(ndz);

    // Derivative damping: steep areas suppress high-frequency detail
    const denom = float(1.0).add(accDx.mul(accDx)).add(accDz.mul(accDz));
    a.addAssign(amp.mul(val).div(denom).mul(guard));
    amp.mulAssign(0.5);

    // Rotation + frequency doubling
    const rx = cp.x.mul(FBM_COS).sub(cp.y.mul(FBM_SIN)).mul(2.0).toVar();
    const rz = cp.x.mul(FBM_SIN).add(cp.y.mul(FBM_COS)).mul(2.0).toVar();
    cp.assign(vec2(rx, rz));
  }

  return a;
});

/**
 * tslRidgedNoise — ridged multifractal noise.
 *
 * Each octave computes a ridged signal from value noise:
 *   signal = (1 - |noise|)²
 *
 * Unrolled to 8 iterations with smoothstep guard.
 *
 * @param p    - vec2 position
 * @param seed - float seed
 * @param oct  - float octave count (runtime; 1–8)
 * @returns float in roughly [0, 1]
 */
export const tslRidgedNoise = /*#__PURE__*/ Fn(([p, seed, oct]: any) => {
  const cp  = vec2(p).toVar();
  const amp = float(1.0).toVar();
  const a   = float(0.0).toVar();

  for (let i = 0; i < 8; i++) {
    const guard = smoothstep(float(i - 0.5), float(i + 0.5), float(oct));

    const n      = tslValueNoise(cp, float(seed));   // [-1, 1]
    const ridge  = abs(n);                            // [0, 1]
    const signal = float(1.0).sub(ridge);             // [0, 1], peak at 0
    const sq     = signal.mul(signal);                // (1 - |n|)²

    a.addAssign(amp.mul(sq).mul(guard));
    amp.mulAssign(0.5);

    // Rotation + frequency doubling
    const rx = cp.x.mul(FBM_COS).sub(cp.y.mul(FBM_SIN)).mul(2.0).toVar();
    const rz = cp.x.mul(FBM_SIN).add(cp.y.mul(FBM_COS)).mul(2.0).toVar();
    cp.assign(vec2(rx, rz));
  }

  return a;
});

// ─── Worley / Cellular Noise ──────────────────────────────────────────────────

/**
 * tslWorleyNoise — Worley (cellular) noise.
 *
 * Searches a 3×3 grid of surrounding integer cells. Each cell has a randomly
 * placed feature point determined by tslHash22(). Returns sqrt(minDist) — the
 * F1 distance. Use `tslWorleyNoiseF2F1` for the edge-centric F2−F1 variant.
 *
 * The 3×3 neighbourhood is statically unrolled (9 iterations).
 *
 * @param p    - vec2 position
 * @param seed - float seed
 * @returns float in [0, ~√2]
 */
export const tslWorleyNoise = /*#__PURE__*/ Fn(([p, seed]: any) => {
  const pv      = vec2(p).toVar();
  const s       = float(seed);
  const pi      = floor(pv).toVar();
  const minDist = float(1e6).toVar();

  // Static unroll of the 3×3 neighbour grid
  const offsets: [number, number][] = [
    [-1, -1], [0, -1], [1, -1],
    [-1,  0], [0,  0], [1,  0],
    [-1,  1], [0,  1], [1,  1],
  ];

  for (const [nx, nz] of offsets) {
    const cellX = pi.x.add(float(nx)).toVar();
    const cellZ = pi.y.add(float(nz)).toVar();

    // Feature point offset via hash22; apply seed offset to allow independent layers
    const seedOff = s.mul(0.1);
    const fp      = tslHash22(vec2(cellX.add(seedOff), cellZ.add(seedOff)));   // vec2 in [0,1]²

    // World-space feature point position
    const fpx = cellX.add(fp.x).toVar();
    const fpz = cellZ.add(fp.y).toVar();

    // Euclidean distance²
    const ddx  = pv.x.sub(fpx);
    const ddz  = pv.y.sub(fpz);
    const dist2 = ddx.mul(ddx).add(ddz.mul(ddz)).toVar();

    minDist.assign(min(minDist, dist2));
  }

  return sqrt(minDist);
});

/**
 * tslWorleyNoiseF2F1 — cellular (F2 − F1) edge noise.
 *
 * Tracks both the nearest (F1) and second-nearest (F2) feature-point
 * distances in the 3×3 neighbourhood, then returns `sqrt(F2) - sqrt(F1)`.
 * The result is near-zero on Voronoi cell edges and larger toward cell
 * centres — the characteristic pattern used for grain textures (sand,
 * scales). Plain `tslWorleyNoise` returns F1 alone, which peaks at cell
 * centres rather than emphasising borders; callers that want edge
 * patterns (e.g. the sand grain path in TerrainPBRMaterial) should use
 * this variant.
 *
 * @param p    - vec2 position
 * @param seed - float seed
 * @returns float (≈ 0 on cell edges, larger inside cells)
 */
export const tslWorleyNoiseF2F1 = /*#__PURE__*/ Fn(([p, seed]: any) => {
  const pv = vec2(p).toVar();
  const s  = float(seed);
  const pi = floor(pv).toVar();
  const f1 = float(1e6).toVar();
  const f2 = float(1e6).toVar();

  const offsets: [number, number][] = [
    [-1, -1], [0, -1], [1, -1],
    [-1,  0], [0,  0], [1,  0],
    [-1,  1], [0,  1], [1,  1],
  ];

  for (const [nx, nz] of offsets) {
    const cellX = pi.x.add(float(nx)).toVar();
    const cellZ = pi.y.add(float(nz)).toVar();
    const seedOff = s.mul(0.1);
    const fp = tslHash22(vec2(cellX.add(seedOff), cellZ.add(seedOff)));
    const ddx = pv.x.sub(cellX.add(fp.x));
    const ddz = pv.y.sub(cellZ.add(fp.y));
    const d2  = ddx.mul(ddx).add(ddz.mul(ddz)).toVar();

    // Maintain (f1 ≤ f2) invariant: if d2 beats f1, the old f1 becomes the
    // new f2. Otherwise, if d2 beats f2, it becomes the new f2.
    const isNewF1 = d2.lessThan(f1);
    const nextF2  = isNewF1.select(f1, min(f2, d2));
    const nextF1  = min(f1, d2);
    f1.assign(nextF1);
    f2.assign(nextF2);
  }

  return sqrt(f2).sub(sqrt(f1));
});
